import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Users, FileText, CreditCard, Activity, TrendingUp, TrendingDown,
  Loader2, CheckCircle, Clock, Wifi, WifiOff, Database, Box,
  Mail, MailOpen, Handshake, DollarSign, ArrowUpRight, ArrowDownRight,
  Minus, RefreshCw, UserPlus, ShieldCheck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// ── Helpers ──────────────────────────────────────────────────────────────
const fmt = (n, decimals = 0) =>
  n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const delta = (today, yday) => {
  if (yday === 0) return today > 0 ? 100 : 0;
  return Math.round(((today - yday) / yday) * 100);
};

const ChangeChip = ({ today, yday }) => {
  const pct = delta(today, yday);
  if (pct === 0) return <span className="text-[11px] text-muted-foreground flex items-center gap-0.5"><Minus className="h-3 w-3" />Same as yesterday</span>;
  const up = pct > 0;
  return (
    <span className={`text-[11px] flex items-center gap-0.5 font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(pct)}% vs yesterday
    </span>
  );
};

const CustomTooltip = ({ active, payload, label, prefix = '', suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg text-sm">
      <p className="text-muted-foreground text-xs mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">
          {p.name}: {prefix}{fmt(p.value, p.name.toLowerCase().includes('volume') ? 2 : 0)}{suffix}
        </p>
      ))}
    </div>
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color, bg, today, yday, sub, testid }) => (
  <Card className="border-border bg-card" data-testid={testid}>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
          <Icon className={`h-4.5 w-4.5 ${color}`} />
        </div>
        {today != null && yday != null && <ChangeChip today={today} yday={yday} />}
      </div>
      <p className="text-2xl font-bold font-mono tracking-tight">{fmt(value)}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

// ── Main Dashboard ─────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchDashboard = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/dashboard`);
      setData(res.data);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Dashboard error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    const iv = setInterval(() => fetchDashboard(true), 30000);
    return () => clearInterval(iv);
  }, []);

  const blockchainStatus = data?.blockchain_status || {};
  const isConnected = blockchainStatus.connected;
  const isDormant = blockchainStatus.is_dormant;

  const openRate = data?.emails_sent > 0
    ? Math.round((data.emails_opened / data.emails_sent) * 100)
    : 0;

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Overview</h2>
            <p className="text-xs text-muted-foreground">Last 7 days · updates every 30s</p>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => fetchDashboard(true)} disabled={refreshing}>
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Refresh'}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* ── Top KPI cards ────────────────────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Merchants" value={data?.total_merchants} color="text-blue-400" bg="bg-blue-500/10" today={data?.new_merchants_today} yday={data?.new_merchants_yday} sub={`+${data?.new_merchants_today ?? 0} today`} testid="stat-merchants" />
              <StatCard icon={DollarSign} label="Total Revenue" value={data?.total_revenue} color="text-emerald-400" bg="bg-emerald-500/10" sub={`${fmt(data?.total_payments)} confirmed TXs`} testid="stat-revenue" />
              <StatCard icon={CreditCard} label="Transactions" value={data?.total_payments} color="text-violet-400" bg="bg-violet-500/10" today={data?.txs_today} yday={data?.txs_yday} sub={`${data?.pending_payments ?? 0} pending`} testid="stat-transactions" />
              <StatCard icon={Handshake} label="Total Partners" value={data?.total_partners} color="text-amber-400" bg="bg-amber-500/10" testid="stat-partners" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={FileText} label="Total Invoices" value={data?.total_invoices} color="text-cyan-400" bg="bg-cyan-500/10" testid="stat-invoices" />
              <StatCard icon={Mail} label="Emails Sent" value={data?.emails_sent} color="text-pink-400" bg="bg-pink-500/10" today={data?.emails_today} yday={data?.emails_yday} sub={`${data?.emails_today ?? 0} today`} testid="stat-emails-sent" />
              <StatCard icon={MailOpen} label="Emails Opened" value={data?.emails_opened} color="text-orange-400" bg="bg-orange-500/10" sub={`${openRate}% open rate`} testid="stat-emails-opened" />
              <StatCard icon={Clock} label="Pending TXs" value={data?.pending_payments} color="text-yellow-400" bg="bg-yellow-500/10" testid="stat-pending" />
            </div>

            {/* ── Charts row 1 — Signups + Transactions ───────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Merchant Signups */}
              <Card className="border-border bg-card" data-testid="chart-signups">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-blue-400" /> New Merchant Signups — 7 Days
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={data?.chart_signups || []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="sgGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="signups" name="Signups" stroke="#60a5fa" strokeWidth={2} fill="url(#sgGrad)" dot={{ r: 3, fill: '#60a5fa' }} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Transactions — count + volume */}
              <Card className="border-border bg-card" data-testid="chart-transactions">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-violet-400" /> Crypto Transactions — 7 Days
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data?.chart_transactions || []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="left" dataKey="count" name="TX Count" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                      <Bar yAxisId="right" dataKey="volume" name="Volume ($)" fill="#34d399" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* ── Charts row 2 — Emails ────────────────────────────────── */}
            <Card className="border-border bg-card" data-testid="chart-emails">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Mail className="h-4 w-4 text-pink-400" /> Email Activity — 7 Days (Sent vs Opened)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={data?.chart_emails || []} margin={{ top: 4, right: 24, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="sent" name="Sent" stroke="#f472b6" strokeWidth={2} dot={{ r: 3, fill: '#f472b6' }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="opened" name="Opened" stroke="#fb923c" strokeWidth={2} strokeDasharray="4 2" dot={{ r: 3, fill: '#fb923c' }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* ── Bottom row — Recent signups + recent TXs + system status ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Recent Merchant Signups */}
              <Card className="border-border bg-card" data-testid="recent-signups">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-blue-400" /> Recent Signups
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {!data?.recent_signups?.length ? (
                    <p className="text-muted-foreground text-xs text-center py-6">No signups yet</p>
                  ) : data.recent_signups.map((m, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                      <div>
                        <p className="text-[13px] font-medium leading-tight">{m.full_name || m.email}</p>
                        <p className="text-[11px] text-muted-foreground">{m.email}</p>
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Recent Transactions */}
              <Card className="border-border bg-card" data-testid="recent-transactions">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4 text-violet-400" /> Recent Transactions
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {!data?.recent_transactions?.length ? (
                    <p className="text-muted-foreground text-xs text-center py-6">No transactions</p>
                  ) : data.recent_transactions.slice(0, 6).map((tx, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                      <div className="flex items-center gap-2">
                        {tx.status === 'confirmed'
                          ? <CheckCircle className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                          : <Clock className="h-3.5 w-3.5 text-amber-400 shrink-0" />}
                        <div>
                          <p className="text-[12px] font-mono font-medium">{tx.amount} {tx.currency}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">{tx.tx_hash?.slice(0, 10)}…</p>
                        </div>
                      </div>
                      <Badge className={`text-[10px] ${tx.status === 'confirmed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'} border`}>
                        {tx.status}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* System Status */}
              <Card className="border-border bg-card" data-testid="system-status">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-400" /> System Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {[
                    {
                      icon: isConnected ? Wifi : isDormant ? Wifi : WifiOff,
                      label: 'Blockchain Listener',
                      status: isConnected ? 'Connected' : isDormant ? 'Standby' : 'Disconnected',
                      color: isConnected ? 'text-emerald-400' : isDormant ? 'text-amber-400' : 'text-red-400',
                      dot: isConnected ? 'bg-emerald-400 animate-pulse' : isDormant ? 'bg-amber-400' : 'bg-red-400',
                    },
                    { icon: Database, label: 'Database', status: 'Healthy', color: 'text-emerald-400', dot: 'bg-emerald-400' },
                    { icon: Box, label: 'Current Block', status: `#${blockchainStatus.current_block?.toLocaleString() || 'N/A'}`, color: 'text-muted-foreground', dot: null },
                    { icon: Activity, label: 'Monitored Wallets', status: String(blockchainStatus.monitored_wallets || 0), color: 'text-foreground', dot: null },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                      <div className="flex items-center gap-2">
                        <item.icon className={`h-3.5 w-3.5 ${item.color}`} />
                        <span className="text-[13px]">{item.label}</span>
                      </div>
                      <span className={`text-[12px] font-medium flex items-center gap-1.5 ${item.color}`}>
                        {item.dot && <span className={`w-1.5 h-1.5 rounded-full ${item.dot}`} />}
                        {item.status}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
